import { trackEvent } from "src/Amplitude";
import { Grid, Typography } from "@mui/material";
import type { Theme } from "@mui/material/styles";
import { createStyles, makeStyles } from "@mui/styles";
import { debounce } from "lodash";
import React from "react";
import BrandArrowDown from "src/icons/BrandArrowDown";
import CalendarIcon from "src/icons/CalendarIcon";
import { DEFAULT_TRANSACTION_PARAMS } from "src/pages/TransactionDesktop";
import api from "src/store/features/api";
import { GetUserCurrencyTransactionsApiArg } from "src/store/features/api/generated";
import useInput from "src/utils/hooks/useInput";
import CountryOrCryptoFlag from "../common/CountryOrCryptoFlag";
import { initialFilterData } from "../reports/utils/ReportTools";
import { CurrencySelection } from "./CurrencySelection";
import { getDate } from "./helperfns";
import Option from "./Option";
import TransactionDateRangePicker from "./TransactionDateRangePicker";
import TransactionSizeInput from "./TransactionSizeInput";
import { FilterData } from "./types";

export const DesktopOptionsStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      backgroundColor: (selected) => (selected ? "#e5e9eb" : "#f8f9f9"),
      border: "1px solid transparent",
      padding: "8px 12px",
      borderRadius: "10px",
      height: "33px",
      "&:hover": {
        backgroundColor: (selected) => (selected ? "#e5e9eb" : "#f8f9f9"),
        border: "1px solid #002035",
      },
      textTransform: "none",
    },
  }),
);

const FilterOptionsStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      padding: "28px 12px 0px 12px",
    },
    transactionInputContainer: {
      width: "fit-content",
    },
    transactionInputTypography: {
      paddingRight: "15px",
    },
    transactionInput: {
      width: "130px",
    },
    toTypography: {
      width: "30px",
      color: "#bdc2c5",
    },
    clearAll: {
      cursor: "pointer",
      "&:hover": {
        color: theme.palette.secondary.main,
      },
    },
    transactionSizeError: {
      color: theme.palette.secondary.main,
      fontSize: "14px",
      padding: "12px 12px",
    },
    confirmButton: {
      borderRadius: "10px",
      height: "43px",
      textTransform: "none",
      border: "1px solid #e5e7e8",
      color: "#7b858b",
      "&:hover": {
        border: "1px solid #002035",
        backgroundColor: "unset",
      },
    },
  }),
);

interface FilterOptionsProps {
  currentParams: GetUserCurrencyTransactionsApiArg;
  isError: boolean;
  refetch: () => void;
  setParams: (newParams: GetUserCurrencyTransactionsApiArg) => void;
  lowHigh: string[];
}

const FilterOptions = (props: FilterOptionsProps) => {
  const { currentParams, isError, refetch, setParams, lowHigh } = props;
  const classes = FilterOptionsStyles();

  const [openCurrencySelection, setOpenCurrencySelection] = React.useState<boolean>(false);
  const [openDates, setOpenDates] = React.useState<boolean>(false);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [currency, setCurrency] = React.useState<string>("");
  const [dateTitle, setDateTitle] = React.useState("Select");
  const [startDate, setStartDate] = React.useState<number | undefined>();
  const [endDate, setEndDate] = React.useState<number | undefined>();
  const [currencyTitle, setCurrencyTitle] = React.useState("All");
  const [currencyIcon, setCurrencyIcon] = React.useState("All");
  const [reportsFilter, setReportsFilter] = React.useState<FilterData>(initialFilterData);
  const [minError, setMinError] = React.useState<boolean>(false);
  const [maxError, setMaxError] = React.useState<boolean>(false);
  const { data: cryptoCurrencies = [] } = api.useGetCryptoCurrenciesQuery();

  const treatedCryptoCurrencies = cryptoCurrencies.filter(
    (el) =>
      el.permissions?.buy === true ||
      el?.permissions?.sell === true ||
      el?.permissions?.deposit === true ||
      el.permissions?.withdraw === true,
  );

  // min and max input properties
  const {
    reset: clearMin,
    reseted: resetterMin,
    value: valueMin,
    inputBlurHandler: BlurHandlerMin,
    valueChangeHandler: changeHandlerMin,
  } = useInput();

  const {
    reset: clearMax,
    reseted: resetterMax,
    value: valueMax,
    inputBlurHandler: BlurHandlerMax,
    valueChangeHandler: changeHandlerMax,
  } = useInput();

  React.useEffect(() => {
    if (!valueMin || !valueMax) {
      setMinError(false);
      setMaxError(false);
    }

    if (valueMax && valueMin && valueMax < valueMin) {
      setMinError(true);
      setMaxError(true);
      return;
    } else {
      setMinError(false);
      setMaxError(false);
    }
  }, [valueMin, valueMax]);

  const dropDownIcon = () => {
    if (currencyIcon === "All") {
      return <BrandArrowDown text="dark" />;
    }
    return <CountryOrCryptoFlag name={currencyIcon} size={15} />;
  };

  const handleFiatCurrencyFilter = (icon: string | null, code: string): void => {
    trackEvent(`Select_Transaction_Page_Select_Currency_${code}`);
    setCurrencyTitle(code);
    setCurrencyIcon(icon ? icon : code);
    setCurrency(code === "All" ? "" : code);
  };

  const handleCurrencySelection = (e: any) => {
    setOpenCurrencySelection((prev) => !prev);
  };

  const handleDates = () => {
    trackEvent("Select_Transaction_Page_Dates_Filter");
    setOpenDates((prev) => !prev);
  };

  const handleClearFilter = () => {
    setCurrencyTitle("All");
    setCurrencyIcon("All");
    setDateTitle("Select");
    clearMin();
    clearMax();
    setParams(DEFAULT_TRANSACTION_PARAMS);
    refetch();
  };

  const handleConfirmation = () => {
    const min = valueMin as number;
    const max = valueMax as number;

    const obj: any = {
      currency: currency,
      startDate,
      endDate,
    };

    if (min && min > 0) {
      obj["startTxSize"] = min;
    }
    if (max && max > 0) {
      obj["endTxSize"] = max;
    }

    setParams(obj);
  };

  const currencyData = React.useMemo(() => {
    if (!treatedCryptoCurrencies) return [];

    const filteredCurrencies = treatedCryptoCurrencies.filter(
      (curr) =>
        curr.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (curr.displayName && curr.displayName.toLowerCase().includes(searchQuery.toLowerCase())),
    );

    return filteredCurrencies.map((curr) => {
      return {
        clickValue: curr.code,
        img: curr.logoUrl ?? curr.code,
        listPrimary: curr.code,
        listSecondary: curr.displayName ?? "",
      };
    });
  }, [treatedCryptoCurrencies, searchQuery]);

  const handleChangeDate = React.useCallback(
    (start: number, end: number) => {
      setStartDate(start);
      setEndDate(end);
      const s = getDate(start);
      const e = getDate(end);
      setDateTitle(`${s} - ${e}`);
    },
    [currentParams.startDate, currentParams.endDate],
  );

  /**
   * Every 1 second after an input entered, fetch data.
   */
  React.useEffect(() => {
    const debounceConfirmation = debounce(function () {
      handleConfirmation();
    }, 1000);

    debounceConfirmation();
  }, [currency, startDate, endDate, valueMin, valueMax]);

  return (
    <Grid container>
      <Grid item container spacing={2} className={classes.container}>
        <Grid item>
          <Option
            name="Select Currency:"
            defaultOption={currencyTitle}
            icon={dropDownIcon()}
            onClick={handleCurrencySelection}
            buttonStyles={DesktopOptionsStyles}
          />

          <CurrencySelection
            open={openCurrencySelection}
            setOpen={setOpenCurrencySelection}
            data={currencyData}
            onSearch={setSearchQuery}
            onClick={handleFiatCurrencyFilter}
            all
            subtitle="Select currency"
          />
        </Grid>

        <Grid item>
          <Option
            name="Dates:"
            defaultOption={dateTitle}
            icon={<CalendarIcon height="15px" />}
            onClick={handleDates}
            buttonStyles={DesktopOptionsStyles}
          />

          <TransactionDateRangePicker
            setReportsFilter={setReportsFilter}
            filterData={reportsFilter}
            openCalendar={openDates}
            setOpenCalendar={setOpenDates}
            callback={handleChangeDate}
          />
        </Grid>

        <Grid item container alignItems="center" className={classes.transactionInputContainer}>
          <Grid item className={classes.transactionInputTypography}>
            <Typography>Transaction size:</Typography>
          </Grid>
          <Grid item className={classes.transactionInput}>
            <TransactionSizeInput
              id="transaction-size-min"
              placeHolder={lowHigh[0]}
              value={valueMin}
              hasError={minError}
              reseted={resetterMin}
              onChange={changeHandlerMin}
              inputBlurHandler={BlurHandlerMin}
            />
          </Grid>
          <Grid item container justifyContent="center" className={classes.toTypography}>
            to
          </Grid>
          <Grid item className={classes.transactionInput}>
            <TransactionSizeInput
              id="transaction-size-max"
              placeHolder={lowHigh[1]}
              value={valueMax}
              hasError={maxError}
              reseted={resetterMax}
              onChange={changeHandlerMax}
              inputBlurHandler={BlurHandlerMax}
            />
          </Grid>
        </Grid>

        <Grid item xs container justifyContent="flex-end" alignItems="center">
          <Typography className={classes.clearAll} onClick={handleClearFilter}>
            <u>Clear All</u>
          </Typography>
        </Grid>

        {/* IF ERROR OCCURS FROM TRANSACTION SIZE */}
        <Grid container sx={{ height: "45px" }}>
          {minError && maxError && (
            <Grid item className={classes.transactionSizeError}>
              The value of your maximum amount is lower than your minimum amount. Please enter a
              valid maximum amount to proceed.
            </Grid>
          )}

          {isError && (
            <Grid item className={classes.transactionSizeError}>
              something went wrong
            </Grid>
          )}
        </Grid>
      </Grid>
      {/*
      <Button
        fullWidth
        onClick={handleConfirmation}
        className={classes.confirmButton}
      >
        Confirm Selections
      </Button> */}
    </Grid>
  );
};

export default FilterOptions;
